import { PERIOD_SLUGS, periodBySlug } from './discovery';

/**
 * Period windows — the single source of truth for what `oggi`,
 * `questo-weekend` and each month slug MEAN as a date range.
 *
 * The computation moved here verbatim from `api/lib/period.ts` (which now
 * re-exports it) so the web app's date filter resolves EXACTLY the window the
 * server resolves for the same slug: a shortcut that maps to a period page
 * must produce the same from/to the SSR page and the feed query use, or the
 * label shown would not be the range delivered.
 *
 * `today` is a Europe/Rome calendar date in `yyyy-MM-dd` (the caller decides
 * which calendar it means); everything below runs on the UTC calendar so the
 * result never depends on the host time zone.
 */
export interface PeriodWindow {
  slug: string;
  label: string;
  from: string;
  to: string;
  suffix: string;
  rangeLabel: string;
  isCurrentOrNext: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = PERIOD_SLUGS.slice(2).map((period) => period.slug);
const dateOnly = (date: Date) => date.toISOString().slice(0, 10);

/** Today è già la data di Roma della richiesta; qui si opera solo sul calendario UTC. */
export function resolvePeriodWindow(slug: string, today: string): PeriodWindow | null {
  const meta = periodBySlug(slug);
  if (!meta || typeof today !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(today)) return null;
  const current = new Date(`${today}T00:00:00Z`);
  if (!Number.isFinite(current.getTime()) || dateOnly(current) !== today) return null;

  let from = current;
  let to = current;
  let suffix = 'oggi';
  let isCurrentOrNext = true;
  if (slug === 'questo-weekend') {
    const weekday = current.getUTCDay();
    const untilSaturday = weekday === 0 ? 0 : (6 - weekday + 7) % 7;
    from = new Date(current.getTime() + untilSaturday * DAY_MS);
    to = new Date(from.getTime() + (weekday === 0 ? 0 : 1) * DAY_MS);
    suffix = 'questo weekend';
  } else if (slug !== 'oggi') {
    const month = MONTHS.indexOf(slug);
    const currentMonth = current.getUTCMonth();
    const year = current.getUTCFullYear() + (month < currentMonth ? 1 : 0);
    from = month === currentMonth ? current : new Date(Date.UTC(year, month, 1));
    to = new Date(Date.UTC(year, month + 1, 0));
    suffix = `di ${slug} ${year}`;
    isCurrentOrNext = month === currentMonth || month === (currentMonth + 1) % 12;
  }

  const fullLabel = (date: Date) =>
    `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  let rangeLabel = fullLabel(from);
  if (dateOnly(from) !== dateOnly(to)) {
    const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
    const sameMonth = sameYear && from.getUTCMonth() === to.getUTCMonth();
    let startLabel: string;
    if (sameMonth) {
      startLabel = String(from.getUTCDate());
    } else if (sameYear) {
      startLabel = `${from.getUTCDate()} ${MONTHS[from.getUTCMonth()]}`;
    } else {
      startLabel = fullLabel(from);
    }
    rangeLabel = `${startLabel}–${fullLabel(to)}`;
  }
  return { ...meta, from: dateOnly(from), to: dateOnly(to), suffix, rangeLabel, isCurrentOrNext };
}
